/* Detects a newly-deployed service worker and forces the tab onto it.
 *
 * public/sw.js already calls self.skipWaiting() + self.clients.claim()
 * unconditionally, so a new worker takes over network requests as soon as
 * it installs - but the JS already loaded into this tab stays the OLD
 * bundle until something actually reloads the page. Browsers also don't
 * reliably re-check sw.js for changes on their own (the spec allows up to
 * 24h between checks), so this also polls registration.update() instead of
 * waiting on that.
 *
 * scripts/stamp-sw.js stamps a fresh VERSION into sw.js on every build, so
 * its bytes always differ from the previous deploy - without that, the
 * browser's own byte-for-byte diff would find nothing to update to.
 */

const UPDATE_EVENT = 'ssm:sw-update'
const POLL_MS = 60_000

/** Dispatched on window right before the forced reload, so a UI component
 * can flag it to the user for the brief moment before the page reloads. */
export function onSwUpdateFlagged(handler: () => void): () => void {
  const listener = () => handler()
  window.addEventListener(UPDATE_EVENT, listener)
  return () => window.removeEventListener(UPDATE_EVENT, listener)
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((registration) => {
      const poll = () => void registration.update().catch(() => {})
      setInterval(poll, POLL_MS)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') poll()
      })
      window.addEventListener('focus', poll)
    }).catch((err) => {
      console.warn('Service worker registration failed:', err)
    })
  })

  // A controllerchange fires whenever the active worker for this page
  // changes - including the very first time a worker ever activates for a
  // fresh tab, when there is nothing stale to replace and reloading would
  // just be a pointless flicker. Only force a reload when this tab was
  // ALREADY under a worker's control when it started (i.e. a real update
  // just took over from a previous one), guarded against firing twice.
  const hadControllerOnLoad = Boolean(navigator.serviceWorker.controller)
  let reloading = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerOnLoad || reloading) return
    reloading = true
    window.dispatchEvent(new Event(UPDATE_EVENT))
    // Brief pause so the "Updating…" flag is actually visible before the
    // reload replaces the page, rather than an instant, jarring flicker.
    setTimeout(() => window.location.reload(), 600)
  })
}
